import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Box, Chip, Typography } from '@mui/material';
import React from 'react';

import { getProduct } from '../subscriptions/catalog';
import { getProductIcon } from '../subscriptions/entitlementIcons';
import { normalizeEntitlementsMap } from '../subscriptions/entitlements';
import { getHighestGrantedStep } from '../subscriptions/purchaseSteps';
import type { EntitlementsMap } from '../subscriptions/types';

export interface HighestSubscriptionTierProps {
  entitlements?: EntitlementsMap | null;
}

const HighestSubscriptionTier: React.FC<HighestSubscriptionTierProps> = ({ entitlements }) => {
  const normalized = normalizeEntitlementsMap(entitlements);
  const highestTier = getHighestGrantedStep(normalized);

  if (!highestTier) {
    return null;
  }

  const product = getProduct(highestTier);
  const Icon = getProductIcon(highestTier);

  if (!product) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        p: 2,
        mb: 2,
        borderRadius: 2,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
      data-testid="highest-subscription-tier"
      data-attr={`highest-tier--${highestTier}`}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'secondary.main',
            color: 'secondary.contrastText',
            flexShrink: 0,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase' }}
          >
            Current subscription
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {product.name}
          </Typography>
        </Box>
      </Box>
      <Chip
        icon={<CheckCircleIcon />}
        label="Active"
        size="small"
        color="success"
        variant="outlined"
        sx={{ flexShrink: 0 }}
      />
    </Box>
  );
};

export default HighestSubscriptionTier;
